// UniversalSelect.tsx
"use client";

import * as React from "react";
import { Box, Wrap, Badge, Spinner } from "@chakra-ui/react";
import { Combobox, useListCollection, useFilter } from "@chakra-ui/react";
import useControllable from "hooks/useControllable/useControllable";

export interface UniversalSelectItem {
  label: string;
  value: string;
}

export interface UniversalSelectProps {
  id: string;
  label: string;
  placeholder?: string;
  items?: UniversalSelectItem[];
  value?: string | string[];
  defaultValue?: string | string[];
  multiple?: boolean;
  searchable?: boolean;
  loading?: boolean;
  disabled?: boolean;
  error?: string;
  onChange?: (value: string | string[]) => void;

  // search / debounce / min chars
  minSearchChars?: number; // default 3
  debounceMs?: number; // default 400
  onSearch?: (search: string) => void;

  // infinite scroll
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

export const UniversalSelect: React.FC<UniversalSelectProps> = (props) => {
  const {
    id,
    label,
    placeholder = "Select...",
    items = [],
    multiple = false,
    searchable = true,
    loading = false,
    disabled = false,
    error,
    minSearchChars = 3,
    debounceMs = 400,
    onSearch,
    hasMore = false,
    onLoadMore,
    loadingMore = false,
  } = props;

  // internal controlled-array
  const [value, setValue] = useControllable<string[]>({
    value: Array.isArray(props.value)
      ? props.value
      : props.value
        ? [props.value]
        : undefined,
    defaultValue: Array.isArray(props.defaultValue)
      ? props.defaultValue
      : props.defaultValue
        ? [props.defaultValue]
        : [],
    onChange: (vals) => {
      if (!props.onChange) return;
      if (multiple) props.onChange(vals);
      else props.onChange(vals[0] ?? "");
    },
  });

  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const containerRef = React.useRef<HTMLDivElement>(null);

  const { contains } = useFilter({ sensitivity: "base" });

  const {
    collection,
    filter,
    set: setCollection,
  } = useListCollection({
    initialItems: items,
    filter: contains,
  });

  // sync external items
  React.useEffect(() => {
    setCollection(items);
  }, [items, setCollection]);

  // click-outside close
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // debounced search for remote
  React.useEffect(() => {
    if (!onSearch) return;
    const trimmed = searchTerm.trim();
    if (trimmed.length < minSearchChars) return;

    const handle = window.setTimeout(() => {
      onSearch(trimmed);
    }, debounceMs);

    return () => window.clearTimeout(handle);
  }, [searchTerm, onSearch, minSearchChars, debounceMs]);

  const handleValueChange = (details: any) => {
    const raw = details.value;
    const arr = Array.isArray(raw) ? raw : [raw];
    setValue(arr);

    if (!multiple) {
      setIsOpen(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || !onLoadMore || loadingMore) return;
    const target = e.currentTarget;
    const threshold = 24;
    if (
      target.scrollTop + target.clientHeight >=
      target.scrollHeight - threshold
    ) {
      onLoadMore();
    }
  };

  const selectedBadges =
    multiple && value.length > 0 ? (
      <Wrap mb={2}>
        {value.map((val) => {
          const item = items.find((i) => i.value === val);
          return (
            <Badge key={val} colorScheme="blue">
              {item?.label ?? val}
            </Badge>
          );
        })}
      </Wrap>
    ) : null;

  return (
    <Box ref={containerRef}>
      <Combobox.Root
        multiple={multiple}
        collection={collection}
        value={value}
        onValueChange={handleValueChange}
        disabled={disabled}
        closeOnSelect={!multiple}
        width="100%"
        open={isOpen}
        name={id}
      >
        {label && <Combobox.Label>{label}</Combobox.Label>}

        {selectedBadges}

        <Combobox.Control
          onClick={() => {
            if (!disabled) setIsOpen((o) => !o);
          }}
        >
          <Combobox.Input
            placeholder={placeholder}
            onChange={(e) => {
              const val = e.target.value;
              setSearchTerm(val);

              if (onSearch) {
                // remote mode
                return;
              }
              if (searchable) {
                filter(val);
              }
            }}
          />
          <Combobox.IndicatorGroup>
            {loading && <Spinner size="xs" />}
            <Combobox.ClearTrigger />
            <Combobox.Trigger />
          </Combobox.IndicatorGroup>
        </Combobox.Control>

        {isOpen && (
          <Combobox.Positioner>
            <Box maxH="260px" overflowY="auto" onScroll={handleScroll}>
              <Combobox.Content>
                <Combobox.Empty>No items found</Combobox.Empty>
                {collection.items.map((item: any) => (
                  <Combobox.Item key={item.value} item={item}>
                    {item.label}
                    <Combobox.ItemIndicator />
                  </Combobox.Item>
                ))}

                {loadingMore && (
                  <Box py={2} textAlign="center">
                    <Spinner size="xs" />
                  </Box>
                )}
              </Combobox.Content>
            </Box>
          </Combobox.Positioner>
        )}

        {error && (
          <Box mt={1} fontSize="sm" color="red.500">
            {error}
          </Box>
        )}
      </Combobox.Root>
    </Box>
  );
};
